package com.devi3east.bashbuddy.dashboard.ui.components.dialogs

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import coil3.compose.SubcomposeAsyncImage
import com.devi3east.bashbuddy.common.domain.models.Address
import com.devi3east.bashbuddy.dashboard.ui.components.SectionCard
import com.devi3east.bashbuddy.dashboard.ui.components.inputFields.InputTextField
import com.devi3east.bashbuddy.dashboard.ui.components.inputFields.NumberTextField
import com.devi3east.bashbuddy.features.common.util.DateTimeInput
import com.devi3east.bashbuddy.features.common.util.FilePicker
import com.devi3east.bashbuddy.features.events.common.data.models.Event
import com.devi3east.bashbuddy.features.events.requests.UpdateDraftEventRequest

@Composable
fun EditEventDialog(
    token: String,
    event: Event,
    onDismiss: () -> Unit,
    onSubmit: (UpdateDraftEventRequest) -> Unit,
    onEventImageUploaded: (String) -> Unit
) {
    var title by remember { mutableStateOf(event.title ?: "") }
    var description by remember { mutableStateOf(event.description ?: "") }
    var address by remember { mutableStateOf(event.address?.line1 ?: "") }
    var city by remember { mutableStateOf(event.address?.city ?: "") }
    var state by remember { mutableStateOf(event.address?.state ?: "") }
    var zipCode by remember { mutableStateOf(event.address?.zipCode ?: "") }

    var startInstant by remember { mutableStateOf(event.startInstant) }
    var endInstant by remember { mutableStateOf(event.endInstant) }

    var imageUrl by remember { mutableStateOf(event.imageUrl ?: "") }
    var attendanceAge by remember { mutableStateOf(event.attendanceAge?.toString() ?: "") }

    var showAgeInfoDialog by remember { mutableStateOf(false) }

    val scrollState = rememberScrollState()

    AlertDialog(
        onDismissRequest = onDismiss,
        modifier = Modifier.imePadding(),
        title = {
            Text(
                text = "Edit Event",
                style = MaterialTheme.typography.titleLarge.copy(fontWeight = FontWeight.SemiBold),
                modifier = Modifier.fillMaxWidth(),
                textAlign = TextAlign.Center
            )
        },
        text = {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .verticalScroll(scrollState)
                    .padding(vertical = 8.dp),
                verticalArrangement = Arrangement.spacedBy(10.dp),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                // Title
                InputTextField(
                    label = "Title",
                    value = title,
                    onValueChange = { title = it },
                    modifier = Modifier.fillMaxWidth()
                )

                // Address
                InputTextField(
                    label = "Address",
                    value = address,
                    onValueChange = { address = it },
                    modifier = Modifier.fillMaxWidth()
                )

                // City
                InputTextField(
                    label = "City",
                    value = city,
                    onValueChange = { city = it },
                    modifier = Modifier.fillMaxWidth()
                )

                // State & Zip
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    InputTextField(
                        label = "State",
                        value = state,
                        onValueChange = { input ->
                            if (input.length <= 2) {
                                state = input.uppercase()
                            }
                        },
                        modifier = Modifier.weight(1f)
                    )

                    NumberTextField(
                        label = "Zip",
                        value = zipCode,
                        onValueChange = { input ->
                            if (input.length <= 5 && input.all { it.isDigit() }) {
                                zipCode = input
                            }
                        },
                        modifier = Modifier.weight(1f),
                        keyboardType = KeyboardType.Phone
                    )
                }

                // Attendance age restriction
                NumberTextField(
                    label = "Age Restriction",
                    value = attendanceAge,
                    onValueChange = { input ->
                        if (input.length <= 2 && (input.isEmpty() || input.all { it.isDigit() })) {
                            attendanceAge = input
                        }
                    },
                    modifier = Modifier.fillMaxWidth(),
                    keyboardType = KeyboardType.Phone
                )

                // Start date/time
                SectionCard(title = "Event Start") {
                    DateTimeInput(
                        instant = startInstant,
                        onDateTimeChange = { startInstant = it }
                    )
                }

                // End date/time
                SectionCard(title = "Event End") {
                    DateTimeInput(
                        instant = endInstant,
                        onDateTimeChange = { endInstant = it }
                    )
                }

                // Description
                InputTextField(
                    label = "Description",
                    value = description,
                    onValueChange = { description = it },
                    modifier = Modifier
                        .fillMaxWidth()
                        .heightIn(min = 300.dp),
                    singleLine = false
                )

                // Image preview + upload
                if (imageUrl.isNotBlank()) {
                    SubcomposeAsyncImage(
                        model = imageUrl,
                        contentDescription = "Event Image",
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(150.dp)
                            .clip(RoundedCornerShape(12.dp)),
                        contentScale = ContentScale.Crop
                    )
                }

                FilePicker(
                    token = token,
                    buttonText = "Select Image",
                    organizationId = event.createdById,
                    relatedTitle = event._id,
                    relatedType = "event",
                    onFileUploaded = { uploadedUrl ->
                        onEventImageUploaded(uploadedUrl)
                        imageUrl = uploadedUrl
                    },
                    onUploadError = { /* handle errors */ },
                    modifier = Modifier.fillMaxWidth()
                )
            }
        },
        confirmButton = {
            Button(
                onClick = {
                    val request = UpdateDraftEventRequest(
                        title = title,
                        description = description,
                        startInstant = startInstant,
                        endInstant = endInstant,
                        isMultiDay = startInstant != endInstant,
                        address = Address(
                            line1 = address,
                            city = city,
                            state = state,
                            zipCode = zipCode
                        ),
                        imageUrl = imageUrl,
                        attendanceAge = attendanceAge.toIntOrNull()
                    )
                    onSubmit(request)
                    onDismiss()
                },
                colors = ButtonDefaults.buttonColors(
                    containerColor = MaterialTheme.colorScheme.primary,
                    contentColor = MaterialTheme.colorScheme.onPrimary
                ),
                shape = RoundedCornerShape(12.dp)
            ) {
                Text(
                    text = "Save",
                    style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.SemiBold))
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text(
                    text = "Cancel",
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.SemiBold)
                )
            }
        },
        shape = RoundedCornerShape(12.dp),
        containerColor = MaterialTheme.colorScheme.surface,
        tonalElevation = 6.dp
    )

    // Info dialog — unified with same styling system
    if (showAgeInfoDialog) {
        InfoAlertDialog(
            title = "Age Restriction",
            message = "Specify the minimum age required to attend this event. Enter 0 if there are no restrictions.\n\n" +
                    "This setting applies only to general event attendance — ticket purchase age requirements are configured separately when adding tickets. " +
                    "Note attendance age restrictions clearly in the event description.",
            onDismiss = { showAgeInfoDialog = false },
            confirmText = "Got it"
        )
    }
}